import { Component, ComponentRef, DestroyRef, OnDestroy, OnInit, ViewChild, ViewContainerRef, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { outputToObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ClientUiAuthSerive } from '@roosevelt/client-ui-lib';
import { EligUiAuthSerive } from '@roosevelt/elig-ui-lib';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { FamilyEnrollmentComponent } from './family-enrollment.component';
import { IClientPlanDetails, IMassActionState } from '../store/massActions/state';
import { getContractResponse } from '../store/massActions/selector';
import { getSelectedAdminPlan } from '../login/store/selectors';
import { ILoginState } from '../login/store/state';
import { getAddMemberFeed } from '../store/member/selector';
import { IMemberState } from '../store/member/state';
import { getStrictUiAuth } from '../authorizations/selector';
import { IAppAuthorizations, IUiAuthorizations } from '../authorizations/state';

@Component({
  selector: 'bmt-family-enrollment',
  template: '<ng-container #familyEnrollmentComponent></ng-container>',
  standalone: false
})
export class FamilyEnrollmentContainer implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly componentRef = signal<ComponentRef<FamilyEnrollmentComponent> | null>(null);

  luxId = 'BMT-FamilyEnrollment-Component' + this.constructor.name;
  @ViewChild('familyEnrollmentComponent', {
    read: ViewContainerRef,
    static: true
  })
  componentArea!: ViewContainerRef;
  readonly getAddMemberFeed = toSignal(this.lux.get(getAddMemberFeed, this.luxId), {
    initialValue: null as {
      subscriberName?: string;
      planAcronym: string | string[];
      clientId: string;
      subClientId: string;
      memberId?: number;
      pseudoSsnInd?: boolean;
      subscriberEnrollmentID?: number;
      asOf: string;
      foundMember?: unknown;
      eligibilityEffectiveDate?: any;
      receivedDate?: string;
      qualifierType?: string;
      memberBenefitType?: string;
      eligibilityStatusReason?: string;
      eligibilityStatus?: string;
    } | null
  });
  readonly getContractResponse = toSignal(this.lux.get(getContractResponse, this.luxId), { initialValue: null as IClientPlanDetails | null });
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly strictUiAuth = toSignal(this.lux.get(getStrictUiAuth, this.luxId), {
    initialValue: {} as IUiAuthorizations | {}
  });

  constructor(
    private readonly clientUiAuth: ClientUiAuthSerive,
    private readonly eligUiAuth: EligUiAuthSerive,
    private readonly route: Router,
    private readonly lux: Lux<{
      massAction: IMassActionState,
      member: IMemberState,
      login: ILoginState,
      appUiAuth: IAppAuthorizations
    }>
  ) {
    effect(() => {
      const componentRef = this.componentRef();
      const feed = this.getAddMemberFeed();
      const adminPlan = this.selectedAdminPlan();

      if (componentRef) {
        componentRef.setInput('selectedAdminPlan', adminPlan);
        componentRef.setInput('familyEnrollDefaults', this.normalizeFamilyEnrollDefaults(feed, adminPlan));
      }
    });

    effect(() => {
      const componentRef = this.componentRef();
      const contractData = this.getContractResponse();

      if (componentRef) {
        const dropdowns = this.createFamilyEnrollDropdowns(contractData);
        componentRef.setInput('clientDetails', contractData);
        componentRef.setInput('eligStatusReasons', dropdowns.eligStatusReasons);
        componentRef.setInput('qualifierTypes', dropdowns.qualifierTypes);
        componentRef.setInput('memberBenefitTypes', dropdowns.memberBenefitTypes);
      }
    });

    effect(() => {
      const uiAuth = this.strictUiAuth() as IUiAuthorizations;
      if (uiAuth?.eligibility) {
        this.eligUiAuth.setStrictAuthorizations(uiAuth.eligibility);
      }
      if (uiAuth?.client) {
        this.clientUiAuth.setStrictAuthorizations(uiAuth.client);
      }
    });
  }

  ngOnInit(): void {
    this.loadComponent();
  }

  private createFamilyEnrollDropdowns(contractResposnse: IClientPlanDetails | null): {
    eligStatusReasons: Array<{ value: string; label: string }>;
    qualifierTypes: Array<{ value: string; label: string }>;
    memberBenefitTypes: Array<{ value: string; label: string }>;
  } {
    if (!contractResposnse) {
      return {
        eligStatusReasons: [],
        qualifierTypes: [],
        memberBenefitTypes: []
      };
    }

    const contract = contractResposnse?.client?.subClient?.contract as any;
    const eligStatusReasons = contract?.eligibilityStatusReasonConfig
      ? this.createDropDownOptions(contract.eligibilityStatusReasonConfig.activeSubscriberEligStatusReasonTypes)
      : [];
    const qualifierTypes = contract?.eligibilityConfig?.specialAttributeTypes
      ? this.createDropDownOptions(contract.eligibilityConfig.specialAttributeTypes)
        .filter(qualifier => qualifier.label === 'Retiree' || qualifier.label === 'COBRA')
      : [];
    const memberBenefitTypes = contract?.benefitsConfig?.memberBenefitTypes
      ? this.createDropDownOptions(contract.benefitsConfig.memberBenefitTypes)
      : [];

    return {
      eligStatusReasons,
      qualifierTypes,
      memberBenefitTypes
    };
  }

  private createDropDownOptions(reasons: Array<{ code: string; value: string }> = []): Array<{ value: string; label: string }> {
    return reasons.map(reason => ({
      value: reason.code,
      label: reason.value
    }));
  }

  private normalizeFamilyEnrollDefaults(feed: any, adminPlan: string): any {
    if (!feed?.memberId) {
      return null;
    }

    return {
      adminPlan,
      asOf: feed.asOf || '',
      clientId: feed.clientId || '',
      eligibilityEffectiveDate: feed.eligibilityEffectiveDate || '',
      eligibilityStatus: feed.eligibilityStatus || 'active',
      eligibilityStatusReason: feed.eligibilityStatusReason || '',
      memberBenefitType: feed.memberBenefitType || '',
      memberId: String(feed.memberId),
      planAcronym: Array.isArray(feed.planAcronym) ? feed.planAcronym[0] : String(feed.planAcronym || ''),
      pseudoSsnInd: feed.pseudoSsnInd === true,
      qualifierType: feed.qualifierType || '',
      receivedDate: feed.receivedDate || '',
      subClientId: feed.subClientId || '',
      subscriberEnrollmentID: feed.subscriberEnrollmentID || '',
      foundMember: feed.foundMember ?? null
    };
  }

  private loadComponent(): void {
    this.componentArea.clear();
    const componentRef = this.componentArea.createComponent(FamilyEnrollmentComponent);
    this.componentRef.set(componentRef);

    outputToObservable(componentRef.instance.setMemberStore)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(storeData => {
        if (!storeData) {
          return;
        }

        const { event, ...memberStore } = storeData;
        this.lux.set(state => state.member, {
          ...memberStore
        });
        if (typeof event !== 'string') {
          this.route.navigate(['details']);
        }
      });
  }


  ngOnDestroy(): void {
    this.componentRef()?.destroy();
    this.componentRef.set(null);
    this.lux.destroy(this.luxId);
  }
}
